/**
 * NationalitySelect — searchable combobox of nationalities. Drop-in
 * replacement for a plain <Input> so users can type to filter and pick
 * from a controlled list. Falls back to "Other" if they need free-text.
 */
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { NATIONALITIES } from '../constants/nationalities';

const NationalitySelect = ({ value, onChange, placeholder = 'Select nationality…', testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-slate-400'
          )}
          data-testid={testid || 'nationality-select'}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search nationality…" />
          <CommandList>
            <CommandEmpty>No nationality found.</CommandEmpty>
            <CommandGroup>
              {NATIONALITIES.map((n) => (
                <CommandItem
                  key={n}
                  value={n}
                  onSelect={(selected) => {
                    // CommandItem lowercases the selected value; pick the
                    // canonical (case-preserved) entry from the list.
                    const canonical = NATIONALITIES.find(
                      (x) => x.toLowerCase() === selected.toLowerCase()
                    ) || selected;
                    onChange(canonical === value ? '' : canonical);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === n ? 'opacity-100' : 'opacity-0')} />
                  {n}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default NationalitySelect;
